import React, { useState, useEffect } from 'react';
import { blogService, gameReviewService, imageService } from '../services';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';
import Header from './Header';

const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="star-rating">
      <div className="stars">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="star filled">★</span>
        ))}
        {hasHalfStar && <span className="star filled">★</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="star">☆</span>
        ))}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [prominentBlogs, setProminentBlogs] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [latestReviews, setLatestReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogsResponse, recentResponse, latestResponse] = await Promise.all([
          blogService.getProminent(),
          gameReviewService.getRecently(),
          gameReviewService.getLatest()
        ]);
        
        // Extract content from the response structure
        const blogs = blogsResponse?.data?.content || [];
        const recent = recentResponse?.data?.content || [];
        const latest = latestResponse?.data?.content || [];
        
        setProminentBlogs(blogs);
        setRecentGames(recent);
        setLatestReviews(latest);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load content. Please try again later.');
        setProminentBlogs([]);
        setRecentGames([]);
        setLatestReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    document.title = 'Game Lim - Home';
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const renderBlogCard = (blog, isLarge = false) => (
    <div
      key={blog.slug}
      className={`blog-card ${isLarge ? 'large' : ''}`}
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/blog/${blog.slug}`)}
    >
      <div className="blog-date">{formatDate(blog.createDate)}</div>
      <img 
        src={imageService.getThumbnail(blog.thumbnailPath)} 
        alt={blog.title} 
        className="blog-image"
      />
      <div className="blog-content">
        <h3>{blog.title}</h3>
        <p className="blog-excerpt">{blog.description}</p>
      </div>
    </div>
  );

  return (
    <>
      <Header />
      <div className="home-page">
        {/* Hero Section */}
        <section className="hero">
          <h1 className="hero-title">Level up your choices: Honest reviews and expert picks for games, apps, movies, and entertainment!</h1>
        </section>

        {/* Blog Section */}
        <section className="blog-section">
          <h2>Blog</h2>
          <div className="blog-grid">
            {prominentBlogs && prominentBlogs.length > 0 ? (
              <>
                <div className="blog-column">
                  {renderBlogCard(prominentBlogs[0], true)}
                </div>
                <div className="blog-column">
                  {prominentBlogs.slice(1, 3).map(blog => renderBlogCard(blog))}
                </div>
              </>
            ) : (
              <p>No blogs available at the moment.</p>
            )}
          </div>
        </section>

        {/* Recently Updated Section */}
        <section>
          <h2>Recently Updated</h2>
          <div className="game-grid">
            {recentGames && recentGames.length > 0 ? (
              recentGames.map((game) => (
                <div
                  key={game.slug}
                  className="game-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/review-game/${game.slug}`)}
                >
                  <img 
                    src={imageService.getThumbnail(game.thumbnailPath)} 
                    alt={game.title} 
                    className="game-image"
                  />
                  <h3>{game.title}</h3>
                  <StarRating rating={game.rateAverage || 0} />
                </div>
              ))
            ) : (
              <p>No recent games available.</p>
            )}
          </div>
        </section>

        {/* Latest Reviews Section */}
        <section className="latest-reviews">
          <h2>Latest Reviews</h2>
          <div className="review-grid">
            {latestReviews && latestReviews.length > 0 ? (
              latestReviews.map((review) => (
                <div
                  key={review.slug}
                  className="review-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/review-game/${review.slug}`)}
                >
                  <img 
                    src={imageService.getThumbnail(review.thumbnailPath)} 
                    alt={review.title} 
                    className="review-image"
                  />
                  <div className="review-content">
                    <div className="review-content-header">
                      <h3>{review.title}</h3>
                      <StarRating rating={review.rateAverage || 0} />
                    </div>
                    <p className="review-excerpt">{review.description}</p>
                  </div>
                </div>
              ))
            ) : (
              <p>No reviews available at the moment.</p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage; 